import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const PRESENT_PATH = "/present";
const LAST_EXPLORE_KEY = "haevn:last-explore-path";

/**
 * Mode is derived from the URL — Presentation Mode is anything under /present.
 * This keeps refresh, deep-link, and back/forward behavior simple.
 *
 * `enterPresent` remembers the current explore path so `exitPresent` can
 * restore it on return, which preserves the investor's place in the portal.
 */
export const useMode = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const mode = useMemo(
    () => (location.pathname.startsWith(PRESENT_PATH) ? "present" : "explore"),
    [location.pathname]
  );

  const enterPresent = useCallback(() => {
    if (!location.pathname.startsWith(PRESENT_PATH)) {
      try {
        window.sessionStorage.setItem(LAST_EXPLORE_KEY, location.pathname + location.search);
      } catch {
        /* ignore quota / disabled storage */
      }
    }
    navigate(PRESENT_PATH);
  }, [navigate, location.pathname, location.search]);

  const exitPresent = useCallback(() => {
    let target = "/";
    try {
      target = window.sessionStorage.getItem(LAST_EXPLORE_KEY) || "/";
    } catch {
      /* ignore */
    }
    navigate(target);
  }, [navigate]);

  const toggleMode = useCallback(() => {
    if (mode === "present") exitPresent();
    else enterPresent();
  }, [mode, enterPresent, exitPresent]);

  return { mode, enterPresent, exitPresent, toggleMode };
};

// Backwards-compatibility shim — older components imported ModeProvider, but the
// provider is no longer needed since mode lives in the URL. This is a no-op
// wrapper kept to avoid touching every import.
export const ModeProvider = ({ children }) => children;
